import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiLock, FiCheckCircle, FiShield, FiArrowRight, FiBrain, FiEye, FiEyeOff } = FiIcons;

const UpdatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se o link de recovery é válido
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Se não houver sessão, o link pode ter expirado ou o Supabase ainda está processando o fragmento
        console.warn("Nenhuma sessão detectada no início do UpdatePassword");
      }
    };
    checkSession();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não coincidem.' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Pequena pausa para garantir que o Supabase resolveu todos os locks internos da URL
      await new Promise(resolve => setTimeout(resolve, 800));

      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });
      
      if (error) throw error;

      setMessage({ type: 'success', text: 'Senha alterada com sucesso! Redirecionando...' });
      
      setTimeout(() => {
        // Forçar logout para limpar qualquer estado residual e exigir login com a nova senha
        supabase.auth.signOut().then(() => {
          navigate('/login', { replace: true });
        });
      }, 2500);

    } catch (error) {
      console.error('Update Password Error:', error);
      // Se for o erro de signal aborted, tentamos avisar o usuário para tentar o botão novamente
      const isAbortError = error.message?.includes('aborted');
      setMessage({ 
        type: 'error', 
        text: isAbortError 
          ? 'Conexão interrompida. Por favor, clique no botão novamente.' 
          : error.message 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-slate-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg shadow-blue-200">
            <SafeIcon icon={FiBrain} className="text-3xl text-white" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 italic uppercase">Nova Senha</h1>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Defina sua nova credencial</p>
        </div>

        {message.text && (
          <div className={`p-4 rounded-xl mb-6 text-xs flex items-start space-x-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiShield} className="mt-0.5 shrink-0" />
            <span className="font-bold">{message.text}</span>
          </div>
        )}

        <form onSubmit={handleUpdate} className="space-y-4">
          <div className="relative">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Nova Senha</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type={showPassword ? "text" : "password"} 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Mínimo 6 caracteres" 
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 p-1">
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>

          <div className="relative">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Confirmar Nova Senha</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type={showPassword ? "text" : "password"} 
                required 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Repita a senha" 
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl transition-all flex items-center justify-center shadow-xl shadow-slate-200 mt-4">
            {isLoading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <span className="uppercase tracking-widest text-xs flex items-center">Atualizar Senha <SafeIcon icon={FiArrowRight} className="ml-2" /></span>}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default UpdatePassword;